import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { parseDuration, PRIORITIES, QUICK_TASK_CATEGORIES } from '@kanbix/shared';
import { RichEditor } from '@/components/rich-editor';
import { ItemKeyLink, StatusBadge, TypeIcon, UserPicker } from '@/components/item-bits';
import { Badge, Button, Dialog, Field, Input, Select, Textarea } from '@/components/ui';
import { ApiError, errorMessage, http } from '@/lib/api';
import { useAiStatus, useAssignable, useComponents, useIssueTypes, useLabels, useProject, useProjects, useReleases } from '@/lib/queries';
import type { Item } from '@/lib/types';
import { useUi } from '@/lib/ui-store';
import { doc } from '@/lib/utils';

interface Duplicate {
  key: string;
  title: string;
  status: Item['status'];
  type: Item['type'];
  score: number;
}

/**
 * Create item (FR-WI-1/4/5, FR-QT-1/2): AI drafting fills fields as editable suggestions (FR-AI-1/2/3, §19.4)
 * and similar open items appear while typing (FR-AI-4). Role fields are shown only to users who may set them
 * (FR-ASG-2).
 */
export function CreateItemDialog() {
  const { create, closeCreate } = useUi();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: projects } = useProjects();
  const [projectKey, setProjectKey] = useState<string>('');
  const [type, setType] = useState('story');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState<object | null>(null);
  const [descVersion, setDescVersion] = useState(0);
  const [priority, setPriority] = useState('medium');
  const [severity, setSeverity] = useState('major');
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [releaseId, setReleaseId] = useState<string>('');
  const [componentId, setComponentId] = useState<string>('');
  const [parentKey, setParentKey] = useState('');
  const [storyPoints, setStoryPoints] = useState('');
  const [estimate, setEstimate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [category, setCategory] = useState('internal');
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const [idea, setIdea] = useState('');
  const [aiRequestId, setAiRequestId] = useState<string | null>(null);
  const [dups, setDups] = useState<Duplicate[]>([]);

  useEffect(() => {
    if (!create.open) return;
    setProjectKey(create.projectKey ?? projects?.[0]?.key ?? '');
    setType(create.type ?? 'story');
    setTitle((create.defaults?.title as string) ?? '');
    setDescription(null);
    setDescVersion((v) => v + 1);
    setAssigneeId(null);
    setReleaseId((create.defaults?.releaseId as string) ?? '');
    setComponentId('');
    setParentKey((create.defaults?.parentKey as string) ?? '');
    setStoryPoints('');
    setEstimate('');
    setDueDate('');
    setLabelIds([]);
    setIdea('');
    setAiRequestId(null);
    setDups([]);
  }, [create.open, create.projectKey, create.type, create.defaults, projects]);

  const { data: project } = useProject(projectKey || undefined);
  const { data: types } = useIssueTypes(projectKey || undefined);
  const { data: users } = useAssignable(projectKey || undefined);
  const { data: releases } = useReleases(projectKey || undefined);
  const { data: components } = useComponents(projectKey || undefined);
  const { data: labels } = useLabels(projectKey || undefined);
  const { data: ai } = useAiStatus(projectKey || undefined);
  const canSetRoles = project?.permissions?.includes('item.set_roles');
  const canAssign = project?.permissions?.includes('item.edit');
  const isQuick = type === 'quick_task';
  const level = types?.find((t) => t.key === type)?.hierarchyLevel ?? 2;

  // Duplicate detection while typing (debounced, FR-AI-4).
  useEffect(() => {
    if (!projectKey || title.trim().length < 6 || isQuick) return setDups([]);
    const t = setTimeout(() => {
      http
        .post<Duplicate[]>('/ai/suggest/duplicates', { projectKey, title })
        .then(setDups)
        .catch(() => setDups([]));
    }, 500);
    return () => clearTimeout(t);
  }, [title, projectKey, isQuick]);

  const draftFeature = type === 'bug' ? 'bug' : type === 'task' || type === 'subtask' ? 'task' : 'story';
  const draft = useMutation({
    mutationFn: () => http.post<{ draft: Record<string, unknown>; aiRequestId: string; duplicates: Duplicate[]; componentOptions: string[] }>(`/ai/draft/${draftFeature}`, { projectKey, idea }),
    onSuccess: (res) => {
      const d = res.draft;
      setTitle(String(d.title ?? ''));
      setAiRequestId(res.aiRequestId);
      setDups(res.duplicates);
      if (draftFeature === 'story') {
        const ac = (d.acceptanceCriteria as string[]) ?? [];
        const sub = (d.subTasks as string[]) ?? [];
        setDescription(doc(`${d.userStory}\n\nAcceptance criteria:\n${ac.map((a) => `• ${a}`).join('\n')}${sub.length ? `\n\nSuggested sub-tasks:\n${sub.map((s) => `• ${s}`).join('\n')}` : ''}`));
        if (d.suggestedStoryPoints) setStoryPoints(String(d.suggestedStoryPoints));
        const comp = components?.find((c) => c.name === d.suggestedComponent);
        if (comp) setComponentId(comp.id);
        const lbl = (labels ?? []).filter((l) => ((d.suggestedLabels as string[]) ?? []).includes(l.name)).map((l) => l.id);
        if (lbl.length) setLabelIds(lbl);
      } else if (draftFeature === 'bug') {
        const steps = (d.stepsToReproduce as string[]) ?? [];
        setDescription(doc(`Steps to reproduce:\n${steps.map((s, i) => `${i + 1}. ${s}`).join('\n')}\n\nExpected: ${d.expected}\nActual: ${d.actual}${d.environment ? `\nEnvironment: ${d.environment}` : ''}`));
        if (d.severity) setSeverity(String(d.severity));
      } else {
        const crit = (d.doneCriteria as string[]) ?? [];
        setDescription(doc(`${d.description}\n\nDone when:\n${crit.map((c) => `• ${c}`).join('\n')}`));
      }
      setDescVersion((v) => v + 1);
      toast.success('AI draft ready — review and edit before saving');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const save = useMutation({
    mutationFn: () =>
      http.post<Item>(`/projects/${projectKey}/items`, {
        type,
        title,
        description: description ?? undefined,
        priority,
        severity: type === 'bug' ? severity : undefined,
        assigneeId: assigneeId ?? undefined,
        releaseId: releaseId || undefined,
        componentId: componentId || undefined,
        parentKey: parentKey.trim() ? parentKey.trim().toUpperCase() : undefined,
        storyPoints: storyPoints ? Number(storyPoints) : undefined,
        originalEstimateMin: estimate ? (parseDuration(estimate) ?? undefined) : undefined,
        dueDate: dueDate || undefined,
        category: isQuick ? category : undefined,
        labelIds: labelIds.length ? labelIds : undefined,
        aiAssisted: !!aiRequestId,
        aiRequestId: aiRequestId ?? undefined,
      }),
    onSuccess: (item) => {
      toast.success(`${item.key} created`, { action: { label: 'Open', onClick: () => navigate(`/items/${item.key}`) } });
      void qc.invalidateQueries({ queryKey: ['items'] });
      void qc.invalidateQueries({ queryKey: ['backlog'] });
      void qc.invalidateQueries({ queryKey: ['board'] });
      closeCreate();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'quick_task_cap') {
        toast.error(err.message, { action: { label: 'Make it a task', onClick: () => setType('task') } });
      } else toast.error(errorMessage(err));
    },
  });

  const estimateInvalid = estimate && parseDuration(estimate) === null;
  const typeOptions = useMemo(() => (types ?? []).filter((t) => t.key !== 'subtask' || parentKey), [types, parentKey]);

  return (
    <Dialog
      open={create.open}
      onOpenChange={(o) => !o && closeCreate()}
      title={isQuick ? 'Create quick task' : 'Create work item'}
      wide
      footer={
        <>
          <Button variant="ghost" onClick={closeCreate}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!projectKey || !title.trim() || !!estimateInvalid}>
            Create
          </Button>
        </>
      }
    >
      <div className="grid gap-4 md:grid-cols-[1fr_220px]">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Project">
              <Select value={projectKey} onChange={(e) => setProjectKey(e.target.value)}>
                {(projects ?? []).map((p) => (
                  <option key={p.id} value={p.key}>
                    {p.name} ({p.key})
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Type">
              <Select value={type} onChange={(e) => setType(e.target.value)}>
                {typeOptions.map((t) => (
                  <option key={t.id} value={t.key}>
                    {t.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          {ai?.features[`${draftFeature}_draft`] && !isQuick && type !== 'epic' && (
            <div className="rounded-md border border-brand/30 bg-brand-soft/50 p-2.5">
              <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-brand">
                <Sparkles className="h-3.5 w-3.5" /> Draft with AI
              </div>
              <div className="flex gap-2">
                <Textarea
                  value={idea}
                  onChange={(e) => setIdea(e.target.value)}
                  placeholder={type === 'bug' ? 'Paste notes or logs: what happened, where, what you expected…' : 'One-line idea, e.g. "agent can preview WhatsApp template before submission"'}
                  className="min-h-[38px]"
                  rows={2}
                />
                <Button variant="outline" onClick={() => draft.mutate()} loading={draft.isPending} disabled={idea.trim().length < 3}>
                  ✦ Draft
                </Button>
              </div>
            </div>
          )}
          <Field label="Title">
            <Input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short, specific summary" />
          </Field>
          {aiRequestId && <Badge className="bg-brand-soft text-brand">✦ AI draft — edit freely</Badge>}
          {!isQuick && (
            <Field label="Description">
              <RichEditor key={descVersion} value={description as never} onChange={setDescription} placeholder="Details, acceptance criteria…" />
            </Field>
          )}
          {dups.length > 0 && (
            <div className="rounded-md border border-warning/40 bg-warning/5 p-2.5">
              <div className="mb-1 text-xs font-medium text-warning">Possible duplicates</div>
              {dups.map((d) => (
                <div key={d.key} className="flex items-center gap-2 py-0.5 text-sm">
                  <TypeIcon type={d.type} />
                  <ItemKeyLink itemKey={d.key} />
                  <span className="truncate">{d.title}</span>
                  <StatusBadge status={d.status} compact />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="space-y-3">
          <Field label="Priority">
            <Select value={priority} onChange={(e) => setPriority(e.target.value)}>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </Field>
          {type === 'bug' && (
            <Field label="Severity">
              <Select value={severity} onChange={(e) => setSeverity(e.target.value)}>
                {['critical', 'major', 'minor', 'trivial'].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </Select>
            </Field>
          )}
          {(canAssign || isQuick) && (
            <Field label="Assignee">
              <UserPicker users={users} value={assigneeId} onChange={setAssigneeId} />
            </Field>
          )}
          {level === 2 && (
            <Field label="Release" hint={!isQuick && !canSetRoles ? 'The Release Manager will assign a developer' : undefined}>
              <Select value={releaseId} onChange={(e) => setReleaseId(e.target.value)}>
                <option value="">No release</option>
                {(releases ?? []).filter((r) => r.status !== 'released').map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {!isQuick && (
            <Field label="Component">
              <Select value={componentId} onChange={(e) => setComponentId(e.target.value)}>
                <option value="">None</option>
                {(components ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {!isQuick && type !== 'epic' && (
            <Field label={level === 3 ? 'Parent (required)' : 'Parent epic'}>
              <Input value={parentKey} onChange={(e) => setParentKey(e.target.value)} placeholder="e.g. KBX-12" />
            </Field>
          )}
          {level === 2 && !isQuick && (type !== 'bug' || project?.settings.storyPointsOnBugs) && (
            <Field label="Story points">
              <Select value={storyPoints} onChange={(e) => setStoryPoints(e.target.value)}>
                <option value="">—</option>
                {[1, 2, 3, 5, 8, 13].map((n) => (
                  <option key={n}>{n}</option>
                ))}
              </Select>
            </Field>
          )}
          <Field label="Estimate" error={estimateInvalid ? 'Use e.g. 2h 30m or 1d' : undefined} hint={isQuick ? `Quick tasks up to ${(project?.settings.quickTaskEstimateCapMin ?? 480) / 60}h` : undefined}>
            <Input value={estimate} onChange={(e) => setEstimate(e.target.value)} placeholder="2h 30m" />
          </Field>
          {isQuick && (
            <Field label="Category">
              <Select value={category} onChange={(e) => setCategory(e.target.value)}>
                {QUICK_TASK_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c.replace('_', '-')}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <Field label="Due date">
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </Field>
          {(labels ?? []).length > 0 && !isQuick && (
            <Field label="Labels">
              <div className="flex flex-wrap gap-1">
                {labels!.map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => setLabelIds((s) => (s.includes(l.id) ? s.filter((x) => x !== l.id) : [...s, l.id]))}
                    className="rounded px-1.5 py-0.5 text-xs"
                    style={{ background: labelIds.includes(l.id) ? l.color : `${l.color}22`, color: labelIds.includes(l.id) ? '#fff' : l.color }}
                  >
                    {l.name}
                  </button>
                ))}
              </div>
            </Field>
          )}
        </div>
      </div>
    </Dialog>
  );
}

export function useItemQuery(key?: string) {
  return useQuery({ queryKey: ['item', key], queryFn: () => http.get<Item>(`/items/${key}`), enabled: !!key });
}
